import { useState, useEffect, useCallback } from 'react';
import {
  reportService,
  RevenueData,
  TenantRevenueItem,
  PlanRevenueItem,
  ReportPeriod,
} from '../services/report.service';

interface UsePlatformRevenueReturn {
  data: RevenueData | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export const usePlatformRevenue = (period: ReportPeriod): UsePlatformRevenueReturn => {
  const [data, setData] = useState<RevenueData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await reportService.getPlatformRevenue(period);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch revenue data'));
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
};

interface UseTopTenantsReturn {
  tenants: TenantRevenueItem[];
  loading: boolean;
  error: Error | null;
}

export const useTopTenants = (period: ReportPeriod): UseTopTenantsReturn => {
  const [tenants, setTenants] = useState<TenantRevenueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await reportService.getPlatformRevenue(period);
        setTenants(result.topTenants || []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch top tenants'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period]);

  return { tenants, loading, error };
};

interface UseRevenueByPlanReturn {
  plans: PlanRevenueItem[];
  loading: boolean;
  error: Error | null;
}

export const useRevenueByPlan = (period: ReportPeriod): UseRevenueByPlanReturn => {
  const [plans, setPlans] = useState<PlanRevenueItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await reportService.getPlatformRevenue(period);
        setPlans(result.revenueByPlan || []);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch revenue by plan'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period]);

  return { plans, loading, error };
};
